import { useState } from "react";
import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import {
  fetchAdsOptimizationDecisions,
  type AdsOptimizationDecisionDto,
} from "../../../helpers/api/ads-optimization";

interface Props {
  customerId: string;
}

const FMT_DATETIME = new Intl.DateTimeFormat("en-US", {
  dateStyle: "long",
  timeStyle: "short",
});

const RULE_LABELS: Record<string, string> = {
  A: "A · BP dominance",
  B: "B · Scale winner",
  B_FIRST: "B · First conv +50%",
  C: "C · Park dead",
  D: "D · Keyword ROAS",
  D_ESCALATE: "D · Keyword pause",
};

const STATUS_STYLES: Record<string, string> = {
  applied: "bg-emerald-50 text-emerald-700",
  skipped_dry_run: "bg-blue-50 text-blue-700",
  skipped_guardrail: "bg-amber-50 text-amber-700",
  skipped_kill_switch: "bg-rose-50 text-rose-700",
  skipped_mcc_readonly: "bg-amber-50 text-amber-700",
  failed: "bg-rose-50 text-rose-700",
};

const STATUS_LABELS: Record<string, string> = {
  applied: "Applied",
  skipped_dry_run: "Skipped (dry-run)",
  skipped_guardrail: "Skipped (guardrail)",
  skipped_kill_switch: "Skipped (kill switch)",
  skipped_mcc_readonly: "Skipped (read-only MCC)",
  failed: "Failed",
};

const MODE_LABELS: Record<string, string> = {
  dry_run: "Dry-run",
  live: "Live",
};

const ACTION_LABELS: Record<string, string> = {
  budget_decrease: "Budget −",
  budget_increase: "Budget +",
  budget_park: "Park",
  budget_reallocate: "Reallocate",
  bid_decrease: "Bid −",
  keyword_pause: "Pause kw",
};

function formatDelta(d: AdsOptimizationDecisionDto): string {
  if (d.action === "keyword_pause") return "(paused)";
  const fmt = (n: number) => `$${n.toFixed(2)}`;
  return `${fmt(d.previousValue)} → ${fmt(d.newValue)}`;
}

export function AdsOptimizationDecisionLog({ customerId }: Props) {
  const [ruleFilter, setRuleFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  const { data, isLoading } = useQuery<AdsOptimizationDecisionDto[]>({
    queryKey: ["ads-optimization", "decisions", customerId],
    queryFn: () => fetchAdsOptimizationDecisions(customerId, { limit: 100 }),
  });

  const filtered = (data ?? []).filter((d) => {
    if (ruleFilter && d.ruleCode !== ruleFilter) return false;
    if (statusFilter && d.mutationStatus !== statusFilter) return false;
    return true;
  });

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm flex flex-col">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border-secondary flex-wrap">
        <h2 className="text-sm font-semibold text-content-primary">
          Decision log
        </h2>
        <div className="flex items-center gap-2 text-xs">
          <select
            value={ruleFilter}
            onChange={(e) => setRuleFilter(e.target.value)}
            className="px-2 py-1 border border-border-secondary rounded text-xs"
          >
            <option value="">All rules</option>
            {Object.entries(RULE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 py-1 border border-border-secondary rounded text-xs"
          >
            <option value="">All statuses</option>
            <option value="applied">Applied</option>
            <option value="skipped_dry_run">Skipped (dry-run)</option>
            <option value="skipped_guardrail">Skipped (guardrail)</option>
            <option value="failed">Failed</option>
          </select>
          <span className="text-content-tertiary">
            {filtered.length} / {data?.length ?? 0}
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="p-4 h-24 bg-gray-50 animate-pulse" />
      ) : filtered.length === 0 ? (
        <div className="p-6 text-center text-xs text-content-tertiary leading-relaxed">
          No decisions yet.
          <br />
          Decisions appear here automatically after the 06:00 UTC daily run,
          or immediately when you click "Run now" above.
        </div>
      ) : (
        <div className="overflow-auto max-h-[480px]">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left text-content-tertiary uppercase tracking-wider text-[10px]">
                <th className="px-3 py-2 font-semibold">When</th>
                <th className="px-3 py-2 font-semibold">Rule</th>
                <th className="px-3 py-2 font-semibold">Target</th>
                <th className="px-3 py-2 font-semibold">Action</th>
                <th className="px-3 py-2 font-semibold">Δ</th>
                <th className="px-3 py-2 font-semibold">Mode</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Diagnosis</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr
                  key={d.id}
                  className="border-t border-border-secondary hover:bg-gray-50/60"
                >
                  <td className="px-3 py-2 whitespace-nowrap text-content-tertiary">
                    {FMT_DATETIME.format(new Date(d.createdAt))}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap font-medium">
                    {RULE_LABELS[d.ruleCode] ?? d.ruleCode}
                  </td>
                  <td className="px-3 py-2 max-w-[220px] truncate">
                    {d.entityType === "keyword" ? (
                      <>
                        <span className="font-mono text-[11px]">
                          {d.keywordText}
                        </span>
                        <span className="text-content-tertiary">
                          {" "}
                          · {d.campaignName}
                        </span>
                      </>
                    ) : (
                      d.campaignName ?? d.campaignId
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {ACTION_LABELS[d.action] ?? d.action}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-[11px]">
                    {formatDelta(d)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-content-tertiary">
                    {MODE_LABELS[d.mode] ?? d.mode}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={clsx(
                        "px-1.5 py-0.5 rounded text-[10px] font-medium",
                        STATUS_STYLES[d.mutationStatus] ??
                          "bg-gray-100 text-content-secondary",
                      )}
                    >
                      {STATUS_LABELS[d.mutationStatus] ?? d.mutationStatus}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-content-secondary leading-tight">
                    {d.diagnosis}
                    {d.mutationError && (
                      <span className="block text-rose-600 mt-0.5">
                        {d.mutationError}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
